import { useCallback, useMemo } from 'react';
import { Link } from 'react-router';

import { FMS_READ_SCOPE } from '#/api/fmsMgmt/fmsReadScope';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { buildVoucherColumns } from '#/modules/finMgmt/vouchers/data/voucherColumns';
import { getVoucherApiErrorMessage } from '#/modules/finMgmt/vouchers/helper/apiError';
import { VOUCHER_LIST_SORT_FIELD_LABELS } from '#/modules/finMgmt/vouchers/helper/voucherStatus';
import useVoucherList from '#/modules/finMgmt/vouchers/hooks/useVoucherList';
import {
  applyTotalAmountRange,
  applyVoucherDateRange,
  createFmsFilterChipChange,
  TOTAL_AMOUNT_FILTER_STATUS_FIELDS,
} from '#/modules/finMgmt/shared/helper/fmsListRangeFilters';
import { useCan } from '#/modules/infra/auth/useCan';

/**
 * 個人憑證列表面板（嵌於個人中心右側；server-side list，無 SideMsg）。
 */
const MyVouchersPanel = () => {
  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
  } = useVoucherList({ scope: FMS_READ_SCOPE.SELF });
  const canCreate = useCan(PERM.FMS_VOUCHER_CREATE);

  const columns = useMemo(
    () => buildVoucherColumns({
      filters: listQuery.filters || {},
      onFilterChange: setFilter,
      onTotalAmountRangeChange: (range) => applyTotalAmountRange(setFilter, range),
      onVoucherDateRangeChange: (range) => applyVoucherDateRange(setFilter, range),
    }),
    [listQuery.filters, setFilter],
  );

  const filterStatusFields = useMemo(
    () => [
      ...buildFilterStatusFieldsFromColumns(columns, {
        filters: listQuery.filters,
      }),
      ...TOTAL_AMOUNT_FILTER_STATUS_FIELDS,
    ],
    [columns, listQuery.filters],
  );

  const handleFilterChipChange = useMemo(() => createFmsFilterChipChange(setFilter), [setFilter]);

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const headerAction = canCreate ? (
    <Button type="button" asChild>
      <Link to={ROUTES.personalCenterVoucherCreate}>新增憑證</Link>
    </Button>
  ) : null;

  return (
    <div
      className={cn(
        'flex min-h-0 w-full flex-col',
        'h-[min(52rem,calc(100vh-10rem))]',
      )}
    >
      <ListTablePanel
        tableKey="fms.voucher.personal.list"
        columns={columns}
        rows={rows}
        pagination={pagination}
        isLoading={isLoading}
        isError={isError}
        errorMessage={
          isError
            ? getVoucherApiErrorMessage(error, '載入憑證失敗')
            : undefined
        }
        onRetry={isError ? () => { refetch(); } : undefined}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSearch={setKeyword}
        onReset={resetListQuery}
        initialKeyword={listQuery.keyword}
        searchPlaceholder="搜尋憑證號碼或備註"
        headerAction={headerAction}
        listQuery={listQuery}
        filterStatusFields={filterStatusFields}
        sortFieldLabels={VOUCHER_LIST_SORT_FIELD_LABELS}
        onFilterChange={handleFilterChipChange}
        onKeywordClear={() => setKeyword('')}
        onSortRemove={removeSortAt}
        onSortingChange={handleSortingChange}
        panelClassName="min-h-0 flex-1"
      />
    </div>
  );
};

export default MyVouchersPanel;
